import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders, logout as authLogout } from '../utils/auth.js';
import API_BASE from '../api.js';
const MIN_WITHDRAWAL = 500;
const PER_PAGE = 10;

const PLAN_NAMES = {
  student_yearly:   'Student Yearly',
  clinician_yearly: 'Clinician Yearly',
};

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const Refer = () => {
  const navigate = useNavigate();
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [upiId, setUpiId]             = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [page, setPage]               = useState(1);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/referral/me`, { headers: getAuthHeaders() });
      if (res.status === 401) { navigate('/app'); return; }
      if (!res.ok) throw new Error('Failed to fetch referral data');
      setData(await res.json());
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [navigate, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label}!`);
    } catch {
      showToast('Copy failed — please copy manually', 'error');
    }
  };

  const shareWhatsApp = () => {
    if (!data) return;
    const msg = encodeURIComponent(
      `Join me on AssessMonk!\n\nUse my referral link and start using AI-powered clinical assessments.\n\nReferral Code: ${data.code}\n\n${data.link}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const shareInstagram = async () => {
    if (!data) return;
    try { await navigator.clipboard.writeText(data.link); } catch { /**/ }
    window.open('https://www.instagram.com', '_blank', 'noopener');
    showToast('Referral link copied. Paste it in your Instagram Story or DM.');
  };

  const handleWithdraw = async () => {
    const amt = parseInt(withdrawAmount, 10);
    if (!amt || amt < MIN_WITHDRAWAL) { showToast(`Minimum withdrawal is ₹${MIN_WITHDRAWAL}`, 'error'); return; }
    if (amt > (data?.wallet?.available_balance || 0)) { showToast('Amount exceeds available balance', 'error'); return; }
    if (!upiId.trim()) { showToast('Please enter your UPI ID', 'error'); return; }
    setWithdrawLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/referral/request-withdrawal`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, upi_id: upiId.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Request failed');
      setShowWithdraw(false);
      setWithdrawAmount('');
      setUpiId('');
      showToast("Withdrawal request submitted! We'll process it within 3–5 business days.");
      fetchData();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const ledger       = data?.ledger || [];
  const totalPages   = Math.ceil(ledger.length / PER_PAGE);
  const visibleLedger = ledger.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const commAmt      = data?.commission_amount || 100;
  const available    = data?.wallet?.available_balance || 0;

  const shareMsg = data
    ? `Join me on AssessMonk!\n\nUse my referral link and start using AI-powered clinical assessments.\n\nReferral Code: ${data.code}\n\n${data.link}`
    : '';

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f6f8fb', fontFamily: "'Inter',-apple-system,sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: '#e2e8f0', margin: '0 auto 12px', animation: 'pulse 1.4s ease-in-out infinite' }} />
        <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>Loading your referral dashboard…</p>
      </div>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.38}}`}</style>
    </div>
  );

  return (
    <>
    <style>{`
      @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
      @keyframes pulse  { 0%,100%{opacity:1}50%{opacity:0.38} }
      .ref-card { background:#fff; border-radius:18px; border:1px solid #eaecf0; box-shadow:0 1px 6px rgba(15,23,42,0.06); padding:22px 24px; }
      .ref-btn  { border:none; border-radius:10px; font-weight:700; cursor:pointer; transition:all 0.15s; display:inline-flex; align-items:center; justify-content:center; gap:7px; font-family:inherit; }
      @media(max-width:700px){
        .ref-grid-2{grid-template-columns:1fr!important;}
        .ref-grid-4{grid-template-columns:1fr 1fr!important;}
        .ref-share-row{flex-direction:column!important;}
        .ref-hide-sm{display:none!important;}
      }
    `}</style>

    <div style={{ minHeight: '100vh', background: '#f6f8fb', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif", color: '#0f172a' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4', border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`, borderRadius: 12, padding: '12px 18px', fontSize: '0.84rem', fontWeight: 600, color: toast.type === 'error' ? '#991b1b' : '#166534', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxWidth: 340, animation: 'fadeIn 0.2s ease-out' }}>
          {toast.type === 'error' ? '⚠ ' : '✓ '}{toast.msg}
        </div>
      )}

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #eaecf0', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/home')}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '-0.01em' }}>AssessMonk</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => navigate('/home')}
            className="ref-btn"
            style={{ background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', padding: '6px 14px', fontSize: '0.78rem', minHeight: 44 }}
            onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
            onMouseLeave={e => e.currentTarget.style.background = '#eef2ff'}
          >← Dashboard</button>
          <button
            onClick={() => authLogout(navigate)}
            className="ref-btn"
            style={{ background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', padding: '6px 14px', fontSize: '0.78rem', minHeight: 44 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
          >Sign out</button>
        </div>
      </nav>

      {/* Page content */}
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '28px 20px 60px', boxSizing: 'border-box' }}>

        {/* Hero */}
        <div style={{ marginBottom: 24, animation: 'fadeIn 0.3s ease-out' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)', borderRadius: 99, padding: '5px 16px', fontSize: '0.68rem', fontWeight: 700, color: '#4f46e5', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>
            🎁 Referral Program
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 'clamp(1.5rem,4vw,2rem)', fontWeight: 900, letterSpacing: '-0.03em', color: '#0f172a' }}>
            Refer &amp; Earn
          </h1>
          <p style={{ margin: 0, fontSize: 'clamp(0.8rem,2vw,0.9rem)', color: '#64748b', lineHeight: 1.65, maxWidth: 500 }}>
            Invite your colleagues to AssessMonk and earn <strong style={{ color: '#4f46e5' }}>₹{commAmt}</strong> commission every time someone becomes a paying customer.
          </p>
        </div>

        {/* Wallet stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }} className="ref-grid-4">
          {[
            { label: 'Available',       value: fmt(data?.wallet?.available_balance), color: '#4f46e5', bg: '#eef2ff', icon: '💰' },
            { label: 'Pending',         value: fmt(data?.wallet?.pending_balance),   color: '#f59e0b', bg: '#fffbeb', icon: '⏳' },
            { label: 'Lifetime Earned', value: fmt(data?.wallet?.total_earned),      color: '#10b981', bg: '#ecfdf5', icon: '📈' },
            { label: 'Paid Out',        value: fmt(data?.wallet?.total_paid),        color: '#64748b', bg: '#f1f5f9', icon: '✅' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 16, border: `1px solid ${s.color}22`, padding: '16px 18px', animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ fontSize: '1.3rem', marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 'clamp(1rem,2.5vw,1.45rem)', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }} className="ref-grid-4">
          {[
            { label: 'Referrals',   value: data?.stats?.total_referrals || 0,      icon: '👥', color: '#2563eb' },
            { label: 'Conversions', value: data?.stats?.successful_referrals || 0, icon: '🎯', color: '#10b981' },
            { label: 'Link Clicks', value: data?.stats?.link_clicks || 0,          icon: '🔗', color: '#8b5cf6' },
            { label: 'Conv. Rate',  value: `${data?.stats?.conversion_rate || '0.0'}%`, icon: '📊', color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 16, border: '1px solid #eaecf0', padding: '16px 18px', textAlign: 'center', animation: 'fadeIn 0.35s ease-out' }}>
              <div style={{ fontSize: '1.2rem', marginBottom: 5 }}>{s.icon}</div>
              <div style={{ fontSize: 'clamp(0.95rem,2.5vw,1.3rem)', fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Code + Link + Share */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }} className="ref-grid-2">

          {/* Left col */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Referral Code */}
            <div className="ref-card">
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Your Referral Code</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#f8fafc', borderRadius: 12, padding: '14px 16px', border: '1.5px dashed #c7d2fe' }}>
                <span style={{ fontSize: 'clamp(1rem,3vw,1.35rem)', fontWeight: 900, letterSpacing: '0.14em', color: '#4f46e5', fontFamily: 'monospace' }}>
                  {data?.code || '—'}
                </span>
                <button
                  className="ref-btn"
                  onClick={() => copy(data?.code || '', 'Code copied')}
                  style={{ background: '#4f46e5', color: '#fff', padding: '7px 15px', fontSize: '0.78rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#4338ca'}
                  onMouseLeave={e => e.currentTarget.style.background = '#4f46e5'}
                >Copy</button>
              </div>
            </div>

            {/* Referral Link */}
            <div className="ref-card">
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Your Referral Link</div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', border: '1px solid #e2e8f0', marginBottom: 10 }}>
                <span style={{ fontSize: '0.7rem', color: '#475569', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  {data?.link || '—'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }} className="ref-share-row">
                <button className="ref-btn" onClick={() => copy(data?.link || '', 'Link copied')}
                  style={{ flex: 1, background: '#f1f5f9', color: '#0f172a', border: '1px solid #e2e8f0', padding: '8px 10px', fontSize: '0.75rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                >📋 Copy Link</button>
                <button className="ref-btn" onClick={shareWhatsApp}
                  style={{ flex: 1, background: '#dcfce7', color: '#166534', border: '1px solid #86efac', padding: '8px 10px', fontSize: '0.75rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#bbf7d0'}
                  onMouseLeave={e => e.currentTarget.style.background = '#dcfce7'}
                >💬 WhatsApp</button>
                <button className="ref-btn" onClick={shareInstagram}
                  style={{ flex: 1, background: 'linear-gradient(135deg,#fce7f3,#ede9fe)', color: '#7c3aed', border: '1px solid #ddd6fe', padding: '8px 10px', fontSize: '0.75rem' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >📸 Instagram</button>
              </div>
            </div>
          </div>

          {/* Right col */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Share message */}
            <div className="ref-card" style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Share Message</div>
                <button className="ref-btn" onClick={() => copy(shareMsg, 'Message copied')}
                  style={{ background: 'transparent', color: '#4f46e5', border: '1px solid #c7d2fe', padding: '4px 12px', fontSize: '0.72rem' }}>Copy</button>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', border: '1px solid #f1f5f9', fontSize: '0.78rem', color: '#334155', lineHeight: 1.72, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {shareMsg}
              </div>
            </div>

            {/* Withdraw */}
            <div className="ref-card" style={{ background: 'linear-gradient(135deg,#f8faff,#eef2ff)', border: '1.5px solid #c7d2fe' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a', marginBottom: 3 }}>Ready to withdraw?</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Min ₹{MIN_WITHDRAWAL} · Processed in 3–5 business days</div>
                </div>
                <button
                  className="ref-btn"
                  onClick={() => setShowWithdraw(true)}
                  disabled={available < MIN_WITHDRAWAL}
                  style={{
                    background: available < MIN_WITHDRAWAL ? '#e2e8f0' : '#4f46e5',
                    color: available < MIN_WITHDRAWAL ? '#94a3b8' : '#fff',
                    padding: '10px 18px', fontSize: '0.82rem',
                    cursor: available < MIN_WITHDRAWAL ? 'not-allowed' : 'pointer',
                    boxShadow: available < MIN_WITHDRAWAL ? 'none' : '0 4px 14px rgba(79,70,229,0.28)',
                  }}
                  onMouseEnter={e => { if (available >= MIN_WITHDRAWAL) e.currentTarget.style.background = '#4338ca'; }}
                  onMouseLeave={e => { if (available >= MIN_WITHDRAWAL) e.currentTarget.style.background = '#4f46e5'; }}
                >Request Payment</button>
              </div>
              {available < MIN_WITHDRAWAL && (
                <div style={{ marginTop: 10, fontSize: '0.7rem', color: '#94a3b8', background: 'rgba(255,255,255,0.6)', borderRadius: 8, padding: '6px 10px' }}>
                  Earn ₹{MIN_WITHDRAWAL - available} more to unlock withdrawals
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transaction Ledger */}
        <div className="ref-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>Transaction History</h2>
              <p style={{ margin: '2px 0 0', fontSize: '0.7rem', color: '#94a3b8' }}>{ledger.length} total commission{ledger.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          {ledger.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '44px 20px' }}>
              <div style={{ width: 44, height: 44, background: '#f1f5f9', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, margin: '0 auto 10px' }}>📋</div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '0.88rem', color: '#475569' }}>No commissions yet</p>
              <p style={{ margin: '4px 0 0', fontSize: '0.76rem', color: '#94a3b8' }}>When someone you referred buys a plan, it'll appear here.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px 90px 110px', padding: '8px 24px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', gap: 8 }}>
                {['Referred User', 'Plan', 'Commission', 'Date'].map((h, i) => (
                  <div key={h} style={{ fontSize: '0.64rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: i > 0 ? 'center' : 'left' }} className={i === 3 ? 'ref-hide-sm' : ''}>{h}</div>
                ))}
              </div>
              {visibleLedger.map((row, i) => (
                <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 90px 110px', padding: '12px 24px', borderBottom: i < visibleLedger.length - 1 ? '1px solid #f8fafc' : 'none', background: i % 2 === 0 ? '#fff' : '#fafafa', alignItems: 'center', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{row.referred_name || row.referred_email}</div>
                    <div style={{ fontSize: '0.66rem', color: '#94a3b8' }}>{row.referred_email}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, background: '#eef2ff', color: '#4f46e5', borderRadius: 6, padding: '3px 8px' }}>
                      {PLAN_NAMES[row.plan_id] || row.plan_id || 'Paid Plan'}
                    </span>
                  </div>
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: '0.88rem', color: '#10b981' }}>
                    {fmt(row.amount)}
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#94a3b8' }} className="ref-hide-sm">
                    {new Date(row.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 24px', borderTop: '1px solid #f1f5f9' }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="ref-btn"
                    style={{ padding: '5px 14px', background: page === 1 ? '#f8fafc' : '#f1f5f9', color: page === 1 ? '#cbd5e1' : '#475569', border: '1px solid #e2e8f0', fontSize: '0.78rem', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>← Prev</button>
                  <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Page {page} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="ref-btn"
                    style={{ padding: '5px 14px', background: page === totalPages ? '#f8fafc' : '#f1f5f9', color: page === totalPages ? '#cbd5e1' : '#475569', border: '1px solid #e2e8f0', fontSize: '0.78rem', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

    {/* Withdrawal Modal */}
    {showWithdraw && (
      <div onClick={() => !withdrawLoading && setShowWithdraw(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(15,23,42,0.58)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: '28px 28px 24px', maxWidth: 420, width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'fadeIn 0.2s ease-out' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>Request Payment</h3>
          <p style={{ margin: '0 0 20px', fontSize: '0.8rem', color: '#64748b' }}>
            Available: <strong style={{ color: '#4f46e5' }}>{fmt(available)}</strong>
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Amount (₹)</label>
            <input
              type="number"
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              placeholder={`Min ₹${MIN_WITHDRAWAL}`}
              min={MIN_WITHDRAWAL}
              max={available}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', fontFamily: 'inherit', color: '#0f172a' }}
              onFocus={e => e.currentTarget.style.borderColor = '#4f46e5'}
              onBlur={e  => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>UPI ID</label>
            <input
              type="text"
              value={upiId}
              onChange={e => setUpiId(e.target.value)}
              placeholder="yourname@upi"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit', color: '#0f172a' }}
              onFocus={e => e.currentTarget.style.borderColor = '#4f46e5'}
              onBlur={e  => e.currentTarget.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ background: '#fffbeb', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: '0.73rem', color: '#92400e', border: '1px solid #fde68a', lineHeight: 1.5 }}>
            ⚠ Payouts are processed manually within 3–5 business days after admin approval.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => !withdrawLoading && setShowWithdraw(false)} className="ref-btn"
              style={{ flex: 1, padding: '11px 0', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>Cancel</button>
            <button onClick={handleWithdraw} disabled={withdrawLoading} className="ref-btn"
              style={{ flex: 2, padding: '11px 0', background: withdrawLoading ? '#c7d2fe' : '#4f46e5', color: '#fff', fontSize: '0.85rem', cursor: withdrawLoading ? 'not-allowed' : 'pointer', boxShadow: withdrawLoading ? 'none' : '0 4px 14px rgba(79,70,229,0.28)' }}>
              {withdrawLoading ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Refer;
